/**
 * Receiving against a purchase order.
 *
 * The warehouse has the supplier's delivery order in hand. This turns it into an RFID order the
 * handheld can guide a count against — the step that used to mean someone running a command-line
 * job, which is why the flow worked in trials but not day to day.
 *
 * Two things shape the screen:
 *
 *   QUANTITY IS THE POINT — what arrived is rarely the whole purchase order, so every line is
 *     editable and defaults to the outstanding amount. Over-stating is refused: the handheld blocks
 *     a scan above plan at submit, so an over-stated line makes an order nobody can finish.
 *   A PUSH IS NOT A DRAFT — the order appears on a handheld immediately, and once anyone scans
 *     against it, it is locked. So the preview is a real step, not a formality.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api, qty } from '../lib/api';
import { Card, Table, Loading, Empty, cell } from '../components/ui';
import { MergedReceive } from './MergedReceive';

type Line = {
  /** The PO line's key. One item can be on several lines in different colours (PO-2609-212). */
  dtlkey?: number;
  itemcode: string;
  uom: string | null;
  outstandingQty: string;
  description: string | null;
  /** What the picker will actually see — a parent code carries no tag. */
  scannableAs: string[];
  /** A charge line — no physical unit to scan, so it is never pushed. */
  nonPhysical?: boolean;
};

type PoView = {
  header: {
    docno: string;
    docdate: string;
    partnerCode: string | null;
    partnerName: string | null;
  };
  orderNo: string;
  existingStatus: number | null;
  lines: Line[];
  unchoosable: string[];
};

type Preview = {
  preview?: boolean;
  orderNo: string;
  payload?: { planQty: number; data: Array<{ sku: string; planQty: number }> };
  excluded?: Array<{ line: { itemcode: string }; reason: string }>;
  dropped?: Array<{ itemcode: string; reason: string }>;
  unchoosable?: string[];
  dryRun?: boolean;
  simulated?: boolean;
  planQty?: number;
  lines?: number;
};

/** Smartlink header status. Anything but 1 means somebody is already working on it. */
/** Quantities are keyed per LINE, so two colour lines of one item are received separately. */
const lineKey = (l: { dtlkey?: number; itemcode: string }): string =>
  l.dtlkey !== undefined ? String(l.dtlkey) : l.itemcode;

const STATUS: Record<number, string> = {
  1: 'pending — nothing scanned yet',
  2: 'being scanned on a handheld',
  3: 'submit failed',
  10: 'submitted and complete',
};

export default function Receive() {
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<Preview | null>(null);
  const [pushed, setPushed] = useState<Preview | null>(null);

  const po = useQuery({
    queryKey: ['receive', submitted],
    enabled: submitted !== '',
    queryFn: () => api.get<PoView>(`/api/v1/rfid/receive/${encodeURIComponent(submitted)}`),
  });

  // Default every line to its full outstanding quantity: receiving everything is the common case,
  // and typing the exceptions is less work than typing the whole delivery.
  useEffect(() => {
    if (!po.data) return;
    setCounted(
      Object.fromEntries(
        po.data.lines.filter((l) => !l.nonPhysical).map((l) => [lineKey(l), l.outstandingQty]),
      ),
    );
    setPreview(null);
    setPushed(null);
  }, [po.data]);

  const send = useMutation({
    mutationFn: (confirm: boolean) =>
      api.post<Preview>('/api/v1/rfid/receive', {
        docno: submitted,
        quantities: counted,
        confirm,
      }),
    onSuccess: (r, confirm) => (confirm ? setPushed(r) : setPreview(r)),
  });

  const locked = po.data?.existingStatus != null && po.data.existingStatus !== 1;
  const total = Object.values(counted).reduce((n, v) => n + (Number(v) || 0), 0);

  /**
   * Every purchase order still awaiting goods.
   *
   * Typing the number assumed you already knew it. A delivery turns up with a supplier DO and the
   * warehouse has to find the matching order — so the list is the starting point and the search box
   * narrows it. Clicking a row loads it below, exactly as typing the number did.
   */
  const [filter, setFilter] = useState('');
  const list = useQuery({
    queryKey: ['receive', 'open-pos'],
    queryFn: () =>
      api.get<{
        miscellaneousHidden?: number;
        purchaseOrders: Array<{
          docno: string;
          docdate: string;
          supplierCode: string | null;
          supplierName: string | null;
          linesOutstanding: number;
          unitsOutstanding: string;
          orderNo: string;
          handheldStatus: number | null;
        }>;
      }>('/api/v1/rfid/receive'),
    refetchInterval: 60_000,
  });

  const needle = filter.trim().toLowerCase();
  const shown = (list.data?.purchaseOrders ?? []).filter(
    (p) =>
      needle === '' ||
      p.docno.toLowerCase().includes(needle) ||
      (p.supplierName ?? '').toLowerCase().includes(needle) ||
      (p.supplierCode ?? '').toLowerCase().includes(needle),
  );

  return (
    <div className="space-y-6">
      <Card
        title="Receive several POs from one supplier"
        subtitle="One handheld session for several of a supplier's purchase orders. The scan is split back into a receipt per PO."
      >
        <MergedReceive purchaseOrders={list.data?.purchaseOrders ?? []} />
      </Card>

      <Card
        title="Receive against a purchase order"
        subtitle="Enter the PO from the supplier's delivery order, set what actually arrived, and send it to the handheld."
      >
        <form
          className="flex flex-wrap items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(code.trim());
          }}
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. PO-2608-207"
            aria-label="Purchase order number"
            className="w-72 rounded border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
          />
          <button
            type="submit"
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Find
          </button>
        </form>

        {po.isLoading && <Loading />}
        {po.isError && (
          <p className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
            {(po.error as Error).message}
          </p>
        )}

        {po.data && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-700">
              <span className="font-mono font-semibold">{po.data.header.docno}</span>{' '}
              <span className="text-slate-500">
                {po.data.header.docdate} ·{' '}
                {po.data.header.partnerName ?? po.data.header.partnerCode}
              </span>
            </p>

            {locked && (
              /* D28: an order with un-submitted scans is locked to us. Re-pushing would not
                 replace it and the scans on the device would be stranded. */
              <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                An order for this PO already exists ({po.data.orderNo}) —{' '}
                <strong>{STATUS[po.data.existingStatus ?? 0] ?? po.data.existingStatus}</strong>.
                {po.data.existingStatus === 2 &&
                  ' Finish or clear it on the device before sending another.'}
              </p>
            )}

            {po.data.lines.length === 0 ? (
              <Empty>Nothing outstanding on this purchase order.</Empty>
            ) : (
              <>
                <Table
                  head={['Item', 'Outstanding', 'Receiving', 'UOM', 'Scannable as']}
                  rows={po.data.lines.map((l) => [
                    cell(
                      l.itemcode,
                      <span>
                        <span className="font-mono text-xs">{l.itemcode}</span>
                        {l.description && (
                          <span className="ml-2 text-xs text-slate-500">{l.description}</span>
                        )}
                      </span>,
                    ),
                    qty(l.outstandingQty),
                    // A charge line gets no quantity box: it is dropped before the push, so an
                    // editable field here would take a number and quietly discard it.
                    l.nonPhysical
                      ? cell('', <span className="text-xs text-slate-400">not received</span>)
                      : cell(
                          counted[lineKey(l)] ?? '',
                          <input
                            value={counted[lineKey(l)] ?? ''}
                            onChange={(e) =>
                              setCounted((c) => ({ ...c, [lineKey(l)]: e.target.value }))
                            }
                            aria-label={`Quantity received for ${l.itemcode}${l.description ? ` ${l.description}` : ''}`}
                            className="w-24 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm focus:border-brand-500 focus:outline-none"
                          />,
                        ),
                    l.uom ?? '—',
                    cell(
                      l.scannableAs.join(','),
                      <span className="text-xs text-slate-500">
                        {l.nonPhysical
                          ? 'charge line — nothing to scan, excluded'
                          : l.scannableAs.length === 1 && l.scannableAs[0] === l.itemcode
                            ? 'itself'
                            : `${l.scannableAs.length} code(s): ${l.scannableAs.slice(0, 3).join(', ')}${l.scannableAs.length > 3 ? '…' : ''}`}
                      </span>,
                    ),
                  ])}
                />

                {po.data.unchoosable.length > 0 && (
                  <p className="rounded border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                    No scannable code could be chosen for: {po.data.unchoosable.join(', ')}. These
                    lines would be unscannable — check the colour mappings first.
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-slate-500">{total} unit(s) to receive</span>
                  <button
                    type="button"
                    disabled={locked || send.isPending}
                    onClick={() => send.mutate(false)}
                    className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {send.isPending && send.variables === false ? 'Checking…' : 'Preview the order'}
                  </button>
                  <button
                    type="button"
                    disabled={locked || !preview || send.isPending}
                    onClick={() => send.mutate(true)}
                    className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                  >
                    {send.isPending && send.variables === true ? 'Sending…' : 'Send to handheld'}
                  </button>
                  {!preview && !locked && (
                    <span className="text-xs text-slate-500">
                      ← preview first; the order reaches a handheld immediately
                    </span>
                  )}
                </div>
              </>
            )}

            {send.isError && (
              <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                {(send.error as Error).message}
              </p>
            )}

            {preview && !pushed && (
              <div className="rounded border border-slate-200 bg-brand-50 px-4 py-3 text-sm">
                <p className="text-slate-700">
                  <strong>{preview.payload?.data.length ?? 0}</strong> plan line(s),{' '}
                  <strong>{preview.payload?.planQty ?? 0}</strong> unit(s) for the picker to scan.
                  {preview.dryRun && (
                    <span className="text-slate-500"> DRY_RUN is on — nothing will be sent.</span>
                  )}
                </p>
                {(preview.dropped?.length ?? 0) > 0 && (
                  <p className="mt-1 text-xs text-slate-600">
                    Left off: {preview.dropped?.map((d) => d.itemcode).join(', ')} — charge lines
                    with nothing physical to scan.
                  </p>
                )}
                {(preview.excluded?.length ?? 0) > 0 && (
                  <p className="mt-1 text-xs text-amber-800">
                    Excluded: {preview.excluded?.map((e) => e.line.itemcode).join(', ')}
                  </p>
                )}
              </div>
            )}

            {pushed && (
              <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                {pushed.simulated ? (
                  <>Simulated — DRY_RUN is on, nothing was sent.</>
                ) : (
                  <>
                    Sent as <strong className="font-mono">{pushed.orderNo}</strong> — {pushed.lines}{' '}
                    line(s), {pushed.planQty} unit(s). It should appear on the handheld now.
                  </>
                )}
              </p>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Purchase orders awaiting goods"
        subtitle="Everything with something still to come, newest first. Click one to receive against it."
      >
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by PO number or supplier"
          aria-label="Filter purchase orders"
          className="mb-3 w-80 rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
        {list.isLoading ? (
          <Loading />
        ) : shown.length === 0 ? (
          <Empty>
            {needle === ''
              ? 'Nothing outstanding — every purchase order has been received in full.'
              : `No purchase order matches “${filter.trim()}”.`}
          </Empty>
        ) : (
          <>
            <Table
              head={['PO', 'Date', 'Supplier', 'Lines', 'Units due', 'Handheld', '']}
              rows={shown.slice(0, 200).map((p) => [
                <span className="font-mono text-xs font-semibold">{p.docno}</span>,
                <span className="text-slate-600">{String(p.docdate).slice(0, 10)}</span>,
                p.supplierName ?? p.supplierCode ?? '—',
                cell(p.linesOutstanding, <span>{p.linesOutstanding}</span>),
                cell(Number(p.unitsOutstanding), <span>{qty(p.unitsOutstanding)}</span>),
                <HandheldBadge status={p.handheldStatus} />,
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                  onClick={() => {
                    setCode(p.docno);
                    setSubmitted(p.docno);
                  }}
                >
                  Receive
                </button>,
              ])}
            />
            {/* Never a silent cap, and never a silent exclusion. */}
            {(list.data?.miscellaneousHidden ?? 0) > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                {list.data?.miscellaneousHidden} non-stock purchase order(s) hidden — the{' '}
                <span className="font-mono">-M###</span> series (labels, printing, services), which
                carries no RFID tags and cannot be received by scanning.
              </p>
            )}
            {shown.length > 200 && (
              <p className="mt-2 text-xs text-slate-500">
                Showing the 200 most recent of {shown.length}. Use the filter to narrow it.
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Where a purchase order has got to on the handheld.
 *
 * `null` is not a failure — it means nobody has pushed this one yet, which is the normal state for
 * most open orders. Smartlink's own header statuses otherwise (CLAUDE.md §4.3): 1 pending,
 * 2 in progress, 3 submit FAILED — that 3 is not in the reference docs and must not read as
 * pending — and 10 submitted.
 */
function HandheldBadge({ status }: { status: number | null }): JSX.Element {
  const map: Record<number, { label: string; cls: string }> = {
    1: { label: 'on handheld', cls: 'bg-brand-100 text-brand-800' },
    2: { label: 'being scanned', cls: 'bg-amber-100 text-amber-800' },
    3: { label: 'submit failed', cls: 'bg-alert/15 text-alert' },
    10: { label: 'submitted', cls: 'bg-emerald-100 text-emerald-800' },
  };
  if (status === null) return <span className="text-xs text-slate-400">not sent</span>;
  const m = map[status] ?? { label: `status ${status}`, cls: 'bg-slate-100 text-slate-700' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
}
